import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FieldTree } from "@angular/forms/signals";
import { Brand, SbCreativeType, StorePage } from "@front/m19-api-client";
import { BrandAssetsService, LandingPage } from "@front/m19-services";
import { ILabelComponent, IRadioComponent, IRadioGroupComponent, SpinnerComponent } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { StoreSelectMenuComponent } from "@m19-board/strategies/sb-create-strategy/store-select-menu/store-select-menu.component";
import { tap } from "rxjs";
import { SbFormatCardStatus, SbFormatCardStatusComponent } from "./sb-format-card-status.component";

export interface SbFormFormatModel {
  sbCreativeType: SbCreativeType;
  sbLandingPage: LandingPage;
  store: StorePage | null;
}

const SB_CREATABLE_FORMATS: SbCreativeType[] = [
  SbCreativeType.manualCollection,
  SbCreativeType.storeSpotlight,
  SbCreativeType.video,
  SbCreativeType.brandVideo,
];

const AD_FORMAT_TITLES: Record<SbCreativeType, string> = {
  [SbCreativeType.productCollection]: "sb-form-ad-format.product_collection_carousel",
  [SbCreativeType.manualCollection]: "sb-form-ad-format.manual_collection",
  [SbCreativeType.storeSpotlight]: "sb-form-ad-format.store_spotlight",
  [SbCreativeType.video]: "sb-form-creative.video",
  [SbCreativeType.brandVideo]: "sb-form-ad-format.brand_video",
};

const AD_FORMAT_DESCRIPTIONS: Record<SbCreativeType, string> = {
  [SbCreativeType.productCollection]: "sb-form-ad-format.carousel_description",
  [SbCreativeType.manualCollection]: "sb-form-ad-format.manual_collection_description",
  [SbCreativeType.storeSpotlight]: "sb-form-ad-format.drive_traffic_to_a_store_including_subpages",
  [SbCreativeType.video]: "sb-form-ad-format.video_description",
  [SbCreativeType.brandVideo]: "sb-form-ad-format.brand_video_description",
};

@Component({
  selector: "app-sb-form-format",
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoDirective,
    IRadioGroupComponent,
    IRadioComponent,
    ILabelComponent,
    StoreSelectMenuComponent,
    SpinnerComponent,
    SbFormatCardStatusComponent,
  ],
  host: {
    class: "block",
  },
  templateUrl: "./sb-form-format.component.html",
})
export class SbFormFormatComponent {
  private readonly brandAssetsService = inject(BrandAssetsService);

  readonly creatableFormats = SB_CREATABLE_FORMATS;
  readonly titles = AD_FORMAT_TITLES;
  readonly descriptions = AD_FORMAT_DESCRIPTIONS;
  readonly SbFormatCardStatus = SbFormatCardStatus;
  readonly SbCreativeType = SbCreativeType;
  readonly LandingPage = LandingPage;

  readonly format = input.required<FieldTree<SbFormFormatModel>>();
  readonly selectedBrand = input<Brand | null>(null);
  readonly disableStoreSpotlight = input(false);
  readonly disableManualCollection = input(false);

  readonly storePageLoading = signal(true);

  private readonly allStorePages = toSignal(
    this.brandAssetsService.storePages$.pipe(tap(() => this.storePageLoading.set(false))),
    { initialValue: [] },
  );
  readonly storePages = computed(() => {
    const brand = this.selectedBrand();
    return this.allStorePages().filter((s) => !brand || s.brandEntityId === brand.brandEntityId);
  });
  readonly spotlightAvailableStores = computed(() =>
    this.storePages().filter((page) => this.isSpotlightAvailable(page, this.storePages())),
  );

  readonly storeRelatedFormatDisabled = computed(() => this.storePages().length <= 0);
  readonly storeSpotlightStoresDisabled = computed(() => this.spotlightAvailableStores().length <= 0);
  readonly storeSpotlightFormatDisabled = computed(() => this.disableStoreSpotlight());

  constructor() {
    effect(() => {
      const _format = this.format().sbCreativeType().value();
      untracked(() => {
        this.selectDefaultFormat();
      });
    });
  }

  selectCreativeType(creativeType?: SbCreativeType) {
    if (!creativeType || this.format().sbCreativeType().value() === creativeType) {
      return;
    }

    if (creativeType === SbCreativeType.brandVideo && this.storeRelatedFormatDisabled()) {
      return;
    }

    if (creativeType === SbCreativeType.storeSpotlight && this.disableStoreSpotlight()) {
      return;
    }

    if (
      creativeType === SbCreativeType.storeSpotlight &&
      (this.storeSpotlightStoresDisabled() || this.storeSpotlightFormatDisabled())
    ) {
      return;
    }

    const landingPage =
      creativeType === SbCreativeType.storeSpotlight || creativeType === SbCreativeType.brandVideo
        ? LandingPage.Store
        : LandingPage.ProductListing;

    this.format().sbCreativeType().value.set(creativeType);
    this.format().sbLandingPage().value.set(landingPage);
    this.format().store().value.set(this.defaultStore(creativeType, landingPage));
  }

  selectLandingPage(landingPage?: LandingPage) {
    if (!landingPage || this.format().sbLandingPage().value() === landingPage) {
      return;
    }

    if (landingPage === LandingPage.Store && this.storeRelatedFormatDisabled()) {
      return;
    }

    this.format().sbLandingPage().value.set(landingPage);
    this.format().store().value.set(this.defaultStore(this.format().sbCreativeType().value(), landingPage));
  }

  selectStorePage(store: StorePage | null | undefined) {
    const next = store ?? null;
    if (this.format().store().value()?.storePageId === next?.storePageId) {
      return;
    }

    this.format().store().value.set(next);
  }

  private selectDefaultFormat() {
    if (!this.disableManualCollection()) {
      return;
    }

    if (!this.storeSpotlightFormatDisabled()) {
      this.selectCreativeType(SbCreativeType.storeSpotlight);
      return;
    }

    this.selectCreativeType(SbCreativeType.video);
  }

  private defaultStore(creativeType: SbCreativeType, landingPage: LandingPage): StorePage | null {
    if (landingPage !== LandingPage.Store) {
      return null;
    }

    const pages = creativeType === SbCreativeType.storeSpotlight ? this.spotlightAvailableStores() : this.storePages();
    return pages[0] ?? null;
  }

  private isSpotlightAvailable(storePage: StorePage, stores: StorePage[]) {
    const isHomePage = storePage.storeId === storePage.storePageId;
    if (!isHomePage) return false;

    const subPages = stores.filter((o) => o.storeId === storePage.storePageId);

    return subPages.length >= 4;
  }
}
